import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useCustomFields } from '@/lib/hooks/crm/useCustomFields';
import type { CrmCustomFieldEntityType, CrmCustomFieldDefinition } from '@/lib/api';

interface CustomFieldEditorProps {
  entityType: CrmCustomFieldEntityType;
  metadata: Record<string, unknown>;
  onChange: (metadata: Record<string, unknown>) => void;
}

/**
 * Dynamic form component that renders custom fields for a given entity type.
 * Reads/writes values from/to the entity's metadata JSONB.
 */
export function CustomFieldEditor({ entityType, metadata, onChange }: CustomFieldEditorProps) {
  const { fields, loading } = useCustomFields(entityType);

  if (loading || fields.length === 0) return null;

  function setValue(fieldName: string, value: unknown) {
    onChange({ ...metadata, [fieldName]: value });
  }

  return (
    <div className="space-y-3">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        Custom Fields
      </p>
      {fields.map((field) => (
        <FieldInput
          key={field.id}
          field={field}
          value={metadata[field.fieldName]}
          onChange={(val) => setValue(field.fieldName, val)}
        />
      ))}
    </div>
  );
}

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: CrmCustomFieldDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const label = field.fieldLabel + (field.isRequired ? ' *' : '');

  switch (field.fieldType) {
    case 'text':
    case 'url':
    case 'email':
    case 'phone':
      return (
        <div className="grid gap-1.5">
          <Label className="text-xs">{label}</Label>
          <Input
            type={
              field.fieldType === 'email' ? 'email' : field.fieldType === 'url' ? 'url' : 'text'
            }
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.fieldType === 'url' ? 'https://...' : ''}
          />
        </div>
      );

    case 'number':
      return (
        <div className="grid gap-1.5">
          <Label className="text-xs">{label}</Label>
          <Input
            type="number"
            value={value != null ? String(value) : ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
          />
        </div>
      );

    case 'date':
      return (
        <div className="grid gap-1.5">
          <Label className="text-xs">{label}</Label>
          <Input
            type="date"
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value || null)}
          />
        </div>
      );

    case 'boolean':
      return (
        <div className="flex items-center gap-2">
          <Checkbox checked={!!value} onCheckedChange={(checked) => onChange(!!checked)} />
          <Label className="text-xs">{label}</Label>
        </div>
      );

    case 'select':
      return (
        <div className="grid gap-1.5">
          <Label className="text-xs">{label}</Label>
          <Select value={(value as string) ?? ''} onValueChange={(v) => onChange(v || null)}>
            <SelectTrigger>
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case 'multi_select': {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="grid gap-1.5">
          <Label className="text-xs">{label}</Label>
          <div className="flex flex-wrap gap-1.5">
            {field.options?.map((opt) => {
              const isSelected = selected.includes(opt);
              return (
                <Badge
                  key={opt}
                  variant={isSelected ? 'default' : 'outline'}
                  className="cursor-pointer text-xs"
                  onClick={() => {
                    if (isSelected) {
                      onChange(selected.filter((s) => s !== opt));
                    } else {
                      onChange([...selected, opt]);
                    }
                  }}
                >
                  {opt}
                </Badge>
              );
            })}
          </div>
        </div>
      );
    }

    default:
      return null;
  }
}
